import React, { useState, useEffect } from 'react';
import { apiUrl } from '../config/api';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import styles from './JobDetail.module.css';

interface JobData {
  id: number;
  title: string;
  organization: string;
  location: string;
  type_label: string;
  close_date: string;
  department: string | null;
  formatted_salary: string;
  vacancy_number: string | null;
  gender_label: string;
  no_of_jobs: number;
  description: string | null;
  contact_email: string | null;
}

const JobDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchJob();
  }, [id]);

  const fetchJob = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiUrl(`/api/jobs/${id}`));
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setJob(result.data);
      } else {
        setError('Failed to fetch job details');
      }
    } catch (err) {
      setError('Failed to load job details');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyEmail = async () => {
    if (!job?.contact_email) return;
    
    try {
      await navigator.clipboard.writeText(job.contact_email);
      setCopied(true);
      toast.success(`📧 Email copied: ${job.contact_email}`);
      
      // Reset copied state after 3 seconds
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      toast.error('Failed to copy email. Please try again.');
      console.error('Copy error:', err);
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading job details...</p>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className={styles.errorState}>
        <p className={styles.errorMessage}>⚠️ {error || 'Job not found'}</p>
        <button onClick={() => navigate('/')} className={styles.backBtn}>
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className={styles.jobDetailContainer}>
      <div className={styles.container}>
        <button onClick={() => navigate(-1)} className={styles.backButton}>
          <i className="fas fa-arrow-left"></i> Back to Jobs
        </button>

        <div className={styles.jobCard}>
          <div className={styles.jobHeader}>
            <h1 className={styles.jobTitle}>{job.title}</h1>
            <span className={`${styles.jobType} ${job.type_label === 'Full Time' ? styles.fullTime : styles.partTime}`}>
              {job.type_label}
            </span>
          </div>

          <div className={styles.jobMeta}>
            <div className={styles.metaItem}>
              <i className="fas fa-building"></i>
              <span><strong>Organization:</strong> {job.organization}</span>
            </div>
            <div className={styles.metaItem}>
              <i className="fas fa-map-marker-alt"></i>
              <span><strong>Location:</strong> {job.location}</span>
            </div>
            {job.department && (
              <div className={styles.metaItem}>
                <i className="fas fa-tag"></i>
                <span><strong>Department:</strong> {job.department}</span>
              </div>
            )}
            <div className={styles.metaItem}>
              <i className="fas fa-money-bill-wave"></i>
              <span><strong>Salary:</strong> {job.formatted_salary || 'Not specified'}</span>
            </div>
            <div className={styles.metaItem}>
              <i className="fas fa-users"></i>
              <span><strong>Gender:</strong> {job.gender_label}</span>
            </div>
            <div className={styles.metaItem}>
              <i className="fas fa-user-friends"></i>
              <span><strong>Positions:</strong> {job.no_of_jobs}</span>
            </div>
            {job.vacancy_number && (
              <div className={styles.metaItem}>
                <i className="fas fa-hashtag"></i>
                <span><strong>Vacancy Number:</strong> {job.vacancy_number}</span>
              </div>
            )}
            <div className={styles.metaItem}>
              <i className="fas fa-calendar"></i>
              <span><strong>Closing Date:</strong> {new Date(job.close_date).toLocaleDateString()}</span>
            </div>
          </div>

          {job.description && (
            <div className={styles.jobDescription}>
              <h3>Job Description</h3>
              <p>{job.description}</p>
            </div>
          )}

          {/* Display email address with copy functionality */}
          {job.contact_email && (
            <div className={styles.emailDisplay}>
              <div className={styles.emailWrapper}>
                <i className="fas fa-envelope"></i>
                <strong>Apply via Email:</strong> 
                <span className={styles.emailAddress}>{job.contact_email}</span>
                <button 
                  onClick={handleCopyEmail}
                  className={`${styles.copySmallBtn} ${copied ? styles.copied : ''}`}
                  title={copied ? "Copied!" : "Copy email"}
                >
                  <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`}></i>
                </button>
                {copied && (
                  <span className={styles.copiedFeedback}>✓ Copied!</span>
                )}
              </div>
            </div>
          )}

          <div className={styles.jobActions}>
            <button onClick={() => navigate('/jobs')} className={styles.homeBtn}>
              <i className="fas fa-home"></i> Return Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetail;